import {HttpErrorResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';

export abstract class Api {

  /**
   * Extract a user-friendly error message from an HTTP error response.
   * Subclasses can override this to handle API-specific error formats.
   */
  static extractErrorMessage(error: HttpErrorResponse): string {
    return 'Something went wrong';
  }

  public get(action: string, params?: object): Observable<any> {
    return this.sendRequest('get', action, undefined, params);
  }

  public post(action: string, body?: object): Observable<any> {
    return this.sendRequest('post', action, body);
  }

  public put(action: string, body?: object): Observable<any> {
    return this.sendRequest('put', action, body);
  }

  public patch(action: string, body?: object): Observable<any> {
    return this.sendRequest('patch', action, body);
  }

  public delete(action: string, params?: object): Observable<any> {
    return this.sendRequest('delete', action, undefined, params);
  }

  public deleteWithBody(action: string, body?: object): Observable<any> {
    return this.sendRequest('delete', action, body);
  }

  public sendRequest(method: string, action: string, body?: object, params?: object): Observable<any> {
    const headers = body ? this.getJsonHeaders() : this.getDefaultHeaders();
    return this.request(headers, method, action, body, params);
  }

  public request(headers: object, method: string, action: string, body?: object, params?: object): Observable<any> {
    let url = this.getUrl();
    if (!this.isValidURL(url)) {
      return throwError(() => new Error('Api url not valid!'));
    }

    if (url.endsWith('/')) {
      url = url.slice(0, -1);
    }

    if (!action.startsWith('/')) {
      action = '/' + action;
    }

    return this.call(method, `${url}${action}`, headers, body, params);
  }

  protected abstract call(method: string, url: string, headers: object, body?: object, params?: object): Observable<any>;

  protected abstract getUrl(): string;

  protected abstract getAccessToken(): string | undefined;

  protected getJsonHeaders(): object {
    return Object.assign(this.getDefaultHeaders(), {'Content-Type': 'application/json'});
  }

  protected getDefaultHeaders(): object {
    const headers: Record<string, string> = {
      'Accept': 'application/json',
    };

    const accessToken = this.getAccessToken();
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    return headers;
  }

  protected createHttpParamsFromObject(object: any): string {
    if (!object) {
      return '';
    }

    const params = new URLSearchParams();
    const appendToParams = (key: string, value: any) => {
      if (value === undefined || value === null || value === 'undefined' || value === 'null') {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((item, index) => appendToParams(`${key}[${index}]`, item));
        return;
      }

      if (typeof value === 'object') {
        for (const property in value) {
          if (Object.prototype.hasOwnProperty.call(value, property)) {
            appendToParams(`${key}[${property}]`, value[property]);
          }
        }
        return;
      }

      params.append(key, value);
    };

    for (const property in object) {
      if (Object.prototype.hasOwnProperty.call(object, property)) {
        appendToParams(property, object[property]);
      }
    }

    return params.toString();
  }

  private isValidURL(string: string): boolean {
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  }
}
